import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TRtkErrorType, useTranslation } from '@packages/utils';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import { RoutesPaths } from 'routes/Routes.types';
import { useGetPeopleByIdQuery } from 'redux/apiSlice/people.slice';
import PageWrapper from 'components/Base/PageWrapper';
import { Tabs } from 'components/Base/Tabs';
import useKeepTabsInUrl from 'hooks/useKeepTabsInUrl';
import Notes from 'components/Shared/Notes';
import { ENoteEntityType } from 'data/types/notes.types';
import OutputTab from 'components/Shared/OutputTab';
import { EPeopleDetailsTabsInUrl } from './PeopleDetails.data';
import DetailsTab from './components/DetailsTab';

const PeopleDetails = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.people',
  });
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = params as { id: string };

  const { data, isLoading, error } = useGetPeopleByIdQuery({
    userId: id,
  });
  const goBackState = (location.state as TBackButtonState) || {
    backUrl: `/app/${RoutesPaths.PEOPLE}`,
    backButtonSuffix: t('list.TITLE'),
  };

  const onBackClick = () => {
    navigate(goBackState.backUrl);
  };

  const isOutputsRoute = location.pathname.endsWith('/inputs-outputs');
  const { tabInUrl } = useKeepTabsInUrl({
    defaultTab: isOutputsRoute
      ? EPeopleDetailsTabsInUrl.OUTPUT
      : EPeopleDetailsTabsInUrl.DETAILS,
    stateInUrl: goBackState,
  });

  const handleTabChange = (key: string) => {
    if (!id) return;
    if (key === EPeopleDetailsTabsInUrl.OUTPUT) {
      navigate(
        `/app/${RoutesPaths.PEOPLE_DETAILS}/inputs-outputs`.replace(':id', id),
      );
    } else {
      const basePath = `/app/${RoutesPaths.PEOPLE_DETAILS}`.replace(':id', id);
      navigate(`${basePath}?tab=${key}`, { state: goBackState });
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: EPeopleDetailsTabsInUrl.DETAILS,
      label: t('details.tabs.DETAILS'),
      children: <DetailsTab data={data} />,
    },
    {
      key: EPeopleDetailsTabsInUrl.NOTES,
      label: t('details.tabs.NOTES', 'Notes'),
      children: <Notes entityType={ENoteEntityType.PEOPLE} entityId={id} />,
    },
    {
      key: EPeopleDetailsTabsInUrl.OUTPUT,
      label: t('details.tabs.OUTPUT', 'Outputs'),
      children: (
        <OutputTab id={id} sourceType="PEOPLE" isWorkshopOrEvent={false} />
      ),
    },
  ];

  return (
    <PageWrapper
      title={data?.fullName}
      backButton={{
        labelPrefix: data?.fullName || '',
        tooltip: goBackState.backButtonSuffix,
        onClick: onBackClick,
      }}
      isLoading={isLoading}
      error={data ? undefined : (error as TRtkErrorType)}
    >
      <Tabs
        defaultActiveKey={tabInUrl}
        onChange={handleTabChange}
        items={tabItems}
      />
    </PageWrapper>
  );
};

export default PeopleDetails;
